import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cx } from '@/lib/cx';
import { qk } from '@/lib/query';
import { Icon } from '@/ui';
import { setLanguage } from '@/i18n';
import {
  getAcademicSessionId,
  getSchoolId,
  getUserInitials,
  selectSchool,
  setAcademicSessionId,
  syncAnalyticsContext,
} from '@/services/auth';
import { fetchAcademicSessions, fetchSchools, type SchoolOption } from '@/services/contextApi';

/**
 * The application's top bar. Ported from v1.
 *
 * It carries the tenant context — which academic session and which school every
 * screen below is scoped to — plus the language toggle and the user avatar.
 *
 * v1 ran inside a frameless Electron window, so this strip also owned the
 * minimise/maximise/close buttons, a macOS inset for the traffic lights, and
 * `-webkit-app-region` drag handling. ui-v2 is a web app; the browser draws its
 * own chrome, and all of that is gone.
 *
 * ── Switching context invalidates every query ──
 * v1 solved this with a `PageRefreshContext` that bumped a `key` and remounted
 * the entire page. Here changing school or session calls
 * `queryClient.invalidateQueries()`, which refetches what is on screen and
 * drops the rest — no remount, no lost scroll position.
 */

/* Shared chip treatment for the two context switchers. */
const chip =
  'rounded-sm border border-chip-line bg-chip text-xs font-medium text-muted transition-colors hover:bg-chip-hover';

/* ── Academic session ───────────────────────────────────────────────────── */

function SessionSwitcher({ onChanged }: { onChanged: () => void }) {
  /* The same key the Sessions register invalidates, so a session added or
     renamed there appears here without a reload. `staleTime: Infinity` means
     nothing refetches it on its own — an explicit invalidation is the only
     thing that should, and now there is one. */
  const sessions = useQuery({
    queryKey: qk.reference.academicSessions,
    queryFn: fetchAcademicSessions,
    staleTime: Infinity,
  });

  /**
   * The selection is derived, not synced. State holds only what the user has
   * explicitly picked this session; everything else falls out of the stored id
   * and the fetched list. Syncing the two in an effect would render one frame
   * with the wrong session selected — and every scoped request fired in that
   * frame would carry the wrong id.
   */
  const [picked, setPicked] = useState<string | null>(null);

  const list = sessions.data;
  const selected = useMemo(() => {
    if (picked) return picked;
    if (!list?.length) return '';
    const stored = getAcademicSessionId();
    if (stored && list.some((s) => s.id === stored)) return stored;
    return (list.find((s) => s.is_current) ?? list[0]).id;
  }, [picked, list]);

  // Writes storage only — no React state, so no cascading render.
  useEffect(() => {
    if (selected && selected !== getAcademicSessionId()) setAcademicSessionId(selected);
  }, [selected]);

  if (!list?.length) return null;

  return (
    <select
      aria-label="Academic session"
      value={selected}
      onChange={(e) => {
        setPicked(e.target.value);
        setAcademicSessionId(e.target.value);
        onChanged();
      }}
      className={cx(chip, 'cursor-pointer appearance-none py-0.5 pl-2 pr-6')}
    >
      {list.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

/* ── School ─────────────────────────────────────────────────────────────── */

function SchoolSwitcher({ onChanged }: { onChanged: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const schools = useQuery({
    queryKey: ['schools', 'switcher'],
    queryFn: fetchSchools,
    staleTime: Infinity,
  });

  /**
   * Derived the same way as the session above. `picked` distinguishes "the
   * user chose All Schools" (`{ id: null }`) from "the user has not chosen"
   * (`null`), which a bare `string | null` cannot express.
   */
  const [picked, setPicked] = useState<{ id: string | null } | null>(null);

  const list = schools.data;
  const selectedId = useMemo(() => {
    if (picked) return picked.id;
    if (!list?.length) return getSchoolId();
    const stored = getSchoolId();
    const current = stored ? list.find((s) => s.id === stored) : undefined;
    // v1 fell back to the first school whenever the stored id was missing or
    // stale, which is why "All Schools" does not survive a reload there.
    // Reproduced deliberately — changing it would change what staff see on
    // startup.
    return current ? current.id : (list[0]?.id ?? null);
  }, [picked, list]);

  // Storage only; keeps the cached school name in step with the id.
  useEffect(() => {
    const school = list?.find((s) => s.id === selectedId);
    if (school) selectSchool(school);
    else if (selectedId === null && picked) selectSchool(null);
  }, [selectedId, list, picked]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const choose = (school: SchoolOption | null) => {
    selectSchool(school);
    setPicked({ id: school?.id ?? null });
    setOpen(false);
    onChanged();
  };

  const selected = list?.find((s) => s.id === selectedId);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cx(chip, 'flex items-center gap-1.5 px-2 py-0.5')}
      >
        <Icon name="schools" size={13} strokeWidth={2} />
        <span className="max-w-36 truncate">{selected ? selected.name : t('common.allSchools')}</span>
        <Icon
          name="chevronDown"
          size={10}
          strokeWidth={2.5}
          className={cx('opacity-60 transition-transform duration-150', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 min-w-52 rounded-item border border-chip-line bg-surface p-1 shadow-dropdown">
          <SchoolOptionRow
            label={t('common.allSchools')}
            active={!selectedId}
            onClick={() => choose(null)}
          />
          {(list?.length ?? 0) > 0 && <div className="my-1 h-px bg-line" />}
          {(list ?? []).map((s) => (
            <SchoolOptionRow
              key={s.id}
              label={s.name}
              code={s.school_code}
              active={s.id === selectedId}
              onClick={() => choose(s)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SchoolOptionRow({
  label,
  code,
  active,
  onClick,
}: {
  label: string;
  code?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left transition-colors',
        active ? 'bg-accent-soft' : 'hover:bg-nav-hover',
      )}
    >
      <span className="flex-1 truncate text-xs font-medium text-ink">{label}</span>
      {code && <span className="text-2xs text-faint">{code}</span>}
      {active && <Icon name="check" size={13} strokeWidth={2.5} className="text-accent" />}
    </button>
  );
}

/* ── Title bar ──────────────────────────────────────────────────────────── */

export function TitleBar() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  /* Changing school or session rescopes every request on the screen — and
     every event too. The tenant ids ride on events as super properties, and
     they were registered once at sign-in and never again, so from the moment a
     clerk switched schools every event they sent was filed under the school
     they had just left. `syncAnalyticsContext` reads the ids the switchers have
     already written to storage. */
  const rescope = () => {
    syncAnalyticsContext();
    void queryClient.invalidateQueries();
  };

  return (
    <header className="flex h-titlebar shrink-0 items-center gap-3 border-b border-line-soft bg-surface px-3">
      <div className="flex shrink-0 items-center gap-2">
        <span className="flex size-7 items-center justify-center rounded-md bg-linear-to-br from-mark-from to-mark-to text-on-accent">
          <Icon name="subjects" size={16} strokeWidth={2} />
        </span>
        <span className="text-base font-bold tracking-tight text-ink">Gurukul</span>

        <SessionSwitcher onChanged={rescope} />
        <SchoolSwitcher onChanged={rescope} />
      </div>

      {/* v1 had a global search box across the middle of this bar. It was never
          wired to anything in v1 either, and a search field that silently does
          nothing is worse than no search field — so it is out until the feature
          exists. It is listed as unbuilt in docs/migration.md. */}

      <div className="ml-auto flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => setLanguage(i18n.language === 'hi' ? 'en' : 'hi')}
          title="Switch language"
          className="flex h-7 items-center gap-1.5 rounded-md border border-chip-line px-2.5 text-2xs font-semibold tracking-wide text-muted transition-colors hover:bg-chip-hover"
        >
          <Icon name="globe" size={15} strokeWidth={2} />
          <span>{i18n.language === 'hi' ? 'हिं' : 'EN'}</span>
        </button>

        <button
          type="button"
          aria-label={t('topBar.notifications')}
          className="flex size-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-chip-hover"
        >
          <Icon name="notice" size={18} strokeWidth={2} />
        </button>

        <span className="flex size-7.5 items-center justify-center rounded-item bg-linear-to-br from-mark-from to-mark-to text-2xs font-bold text-on-accent">
          {getUserInitials()}
        </span>
      </div>
    </header>
  );
}
